import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet';
import 'bootstrap/dist/css/bootstrap.min.css';
import {  services4 } from '../assets';

import FAQsection from './FAQSection';
const NetSuiteCRM = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false
  });

  React.useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const featureItem = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  // Color themes for cards
  const cardThemes = [
    { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', icon: 'bi-people-fill' },
    { bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', icon: 'bi-graph-up-arrow' },
    { bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', icon: 'bi-headset' },
    { bg: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)', icon: 'bi-lightbulb-fill' },
    { bg: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)', icon: 'bi-megaphone-fill' },
    { bg: 'linear-gradient(135deg, #ffc3a0 0%, #ffafbd 100%)', icon: 'bi-cloud-fill' }
  ];

  return (
    <div className="overflow-hidden">
      <Helmet>
        <title>NetSuite CRM Solutions USA | SpireNest</title>
        <link rel="canonical" href="https://spirenest.co/netsuite/services/crm" />
        <meta name="description" content="Enhance customer relationships with NetSuite CRM from SpireNest. Complete CRM implementation and support for US businesses." />
        <meta name="keywords" content="NetSuite CRM, CRM solutions, customer management, USA, SpireNest" />
      </Helmet>
      {/* Hero Section with animated background */}
      <section
        className="text-white position-relative d-flex align-items-center"
        style={{
            background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
            overflow: 'hidden',
            minHeight: '92vh', // Full viewport height
            padding: '60px 0'
        }}
        >
        {/* Animated floating circles */}
        <motion.div
            className="position-absolute rounded-circle"
            style={{
            width: '300px',
            height: '300px',
            top: '-100px',
            right: '-100px',
            background: 'rgba(255, 255, 255, 0.1)'
            }}
            animate={{ y: [0, -20, 0], x: [0, 20, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />

        <motion.div
            className="position-absolute rounded-circle"
            style={{
            width: '200px',
            height: '200px',
            bottom: '-50px',
            left: '-50px',
            background: 'rgba(255, 255, 255, 0.1)'
            }}
            animate={{ y: [0, 20, 0], x: [0, -20, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />

        <Container>
            <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0 text-center text-lg-start">
                <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                >
                <h1 className="display-4 fw-bold mb-4">NetSuite CRM</h1>
                <p className="lead mb-4">
                    Optimize customer experiences with NetSuite CRM—streamline interactions, personalize engagement, 
                    and drive growth with real-time insights and automation.
                </p>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                    variant="light"
                    size="lg"
                    className="rounded-pill px-4 py-2 shadow fw-bold"
                    >
                       <a href="/contact" className="btn  btn-lg px-4">
                       Talk to Our Experts <i className="bi bi-arrow-right ms-2"></i>
                      </a>
                    
                    </Button>
                </motion.div>
                </motion.div>
            </Col>
            <Col lg={6} className="text-center">
                <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                >
                <motion.div
                    animate={{ y: [0, -15, 0] }}
                    transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                >
                    <img
                    src={services4}
                    alt="NetSuite CRM Dashboard"
                    className="img-fluid rounded "
                    />
                </motion.div>
                </motion.div>
            </Col>
            </Row>
        </Container>
        </section>


      {/* Why NetSuite CRM Section */}
      <section className="py-5 bg-light" ref={ref}>
        <Container>
          <motion.div
            initial="hidden"
            animate={controls}
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn}>
              <h2 className="text-center mb-5 fw-bold">Why NetSuite CRM</h2>
              <p className="text-center mb-5 lead">
                Elevate customer relationships with NetSuite CRM, the cloud platform designed to improve sales and satisfaction. 
                NetSuite CRM provides a comprehensive, real-time view of every customer touchpoint.
              </p>
            </motion.div>

            <Row className="g-4">
              {[
                {
                  title: "All-in-One Customer View",
                  content: "NetSuite CRM delivers a real-time, 360-degree view of every customer interaction from first contact to long-term support."
                },
                {
                  title: "Streamlined Lead Management",
                  content: "Easily manage leads, opportunities, and sales processes with precision and efficiency."
                },
                {
                  title: "Enhanced Customer Support",
                  content: "Track communication seamlessly to boost customer service and responsiveness."
                },
                {
                  title: "Smart Customer Insights",
                  content: "Gain instant insights to personalize engagement and build lasting customer loyalty."
                },
                {
                  title: "Automated Marketing",
                  content: "Launch and manage marketing campaigns effortlessly while monitoring performance across all platforms."
                },
                {
                  title: "Scalable Cloud Access",
                  content: "Access your CRM anytime, anywhere, with a cloud platform that grows with your business."
                }
              ].map((feature, index) => (
                <Col md={6} lg={4} key={index}>
                  <motion.div 
                    variants={featureItem}
                    whileHover={{ y: -10 }}
                  >
                    <Card className="h-100 border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px', backgroundColor: 'white', minHeight: '280px' }}>
                      <div style={{ 
                        height: '5px',
                        background: cardThemes[index].bg
                      }}></div>
                      <Card.Body className="p-4 d-flex flex-column">
                        <div className="d-flex align-items-center justify-content-center mb-3" 
                          style={{ 
                            width: '60px', 
                            height: '60px', 
                            borderRadius: '15px',
                            background: cardThemes[index].bg,
                            color: 'white',
                            fontSize: '1.5rem'
                          }}>
                          <i className={`bi ${cardThemes[index].icon}`}></i>
                        </div>
                        <h5 className="fw-bold mb-3 text-black">{feature.title}</h5>
                        <p className="text-muted flex-grow-1">{feature.content}</p>
                       
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Core Features Section */}
      <section className="py-5">
        <Container>
            <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            >
            <h2 className="text-center mb-5 fw-bold">Core Features of NetSuite CRM</h2>
            
            <div className="overflow-hidden py-4">
                <motion.div
                className="d-flex"
                animate={{
                    x: ['0%', '-100%']
                }}
                transition={{
                    duration: 30,
                    repeat: Infinity,
                    ease: "linear"
                }}
                >
                {[
                    { title: "Sales Force Automation", icon: "bi-lightning-charge-fill", color: "#ff6b6b" },
                    { title: "Partner Relationship Management", icon: "bi-shuffle", color: "#74b9ff" },
                    { title: "Marketing Automation", icon: "bi-megaphone-fill", color: "#a29bfe" },
                    { title: "Mobile Accessibility", icon: "bi-phone-fill", color: "#55efc4" },
                    { title: "Customer Service", icon: "bi-headset", color: "#fd79a8" },
                    { title: "Reporting & Analytics", icon: "bi-graph-up-arrow", color: "#ffeaa7" },
                    // Duplicate items for seamless looping
                    { title: "Sales Force Automation", icon: "bi-lightning-charge-fill", color: "#ff6b6b" },
                    { title: "Partner Relationship Management", icon: "bi-shuffle", color: "#74b9ff" },
                    { title: "Marketing Automation", icon: "bi-megaphone-fill", color: "#a29bfe" },
                    { title: "Mobile Accessibility", icon: "bi-phone-fill", color: "#55efc4" },
                    { title: "Customer Service", icon: "bi-headset", color: "#fd79a8" },
                    { title: "Reporting & Analytics", icon: "bi-graph-up-arrow", color: "#ffeaa7" }
                ].map((feature, index) => (
                    <motion.div 
                    key={index}
                    className="mx-4 text-center"
                    style={{ minWidth: '200px' }}
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                    >
                    <div
                        className="mx-auto mb-2"
                        style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: feature.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1.5rem'
                        }}
                    >
                        <i className={feature.icon}></i>
                    </div>
                    <h6 className="fw-bold mb-0">{feature.title}</h6>
                    </motion.div>
                ))}
                </motion.div>
            </div>
            
            {/* Second marquee row moving in opposite direction */}
            <div className="overflow-hidden py-4">
                <motion.div
                className="d-flex"
                animate={{
                    x: ['-100%', '0%']
                }}
                transition={{
                    duration: 35,
                    repeat: Infinity,
                    ease: "linear"
                }}
                >
                {[
                    { title: "Lead Management", icon: "bi-funnel-fill", color: "#a18cd1" },
                    { title: "Customer Insights", icon: "bi-lightbulb-fill", color: "#ffc3a0" },
                    { title: "Workflow Automation", icon: "bi-gear-fill", color: "#43e97b" },
                    { title: "Real-time Analytics", icon: "bi-speedometer2", color: "#667eea" },
                    { title: "Cloud Access", icon: "bi-cloud-fill", color: "#f093fb" },
                    { title: "Custom Dashboards", icon: "bi-grid-fill", color: "#ff9a9e" },
                    // Duplicate items for seamless looping
                    { title: "Lead Management", icon: "bi-funnel-fill", color: "#a18cd1" },
                    { title: "Customer Insights", icon: "bi-lightbulb-fill", color: "#ffc3a0" },
                    { title: "Workflow Automation", icon: "bi-gear-fill", color: "#43e97b" },
                    { title: "Real-time Analytics", icon: "bi-speedometer2", color: "#667eea" },
                    { title: "Cloud Access", icon: "bi-cloud-fill", color: "#f093fb" },
                    { title: "Custom Dashboards", icon: "bi-grid-fill", color: "#ff9a9e" }
                ].map((feature, index) => (
                    <motion.div 
                    key={index}
                    className="mx-4 text-center"
                    style={{ minWidth: '200px' }}
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                    >
                    <div
                        className="mx-auto mb-2"
                        style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: feature.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1.5rem'
                        }}
                    >
                        <i className={feature.icon}></i>
                    </div>
                    <h6 className="fw-bold mb-0">{feature.title}</h6>
                    </motion.div>
                ))}
                </motion.div>
            </div>
            </motion.div>
        </Container> 
      </section>

      {/* Benefits Section */}
      <section className="py-5 bg-light">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn}>
              <h2 className="text-center mb-5 fw-bold">Benefits of NetSuite CRM</h2>
            </motion.div>

            <Row className="g-4">
              <Col lg={6}>
                <motion.div 
                  variants={featureItem}
                  whileHover={{ scale: 1.01 }}
                >
                  <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '15px', backgroundColor: 'white', minHeight: '220px' }}>
                    <Card.Body className="p-4 d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <div className="me-3" style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '12px',
                          background: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '1.2rem'
                        }}>
                          <i className="bi bi-eye-fill"></i>
                        </div>
                        <h3 className="fw-bold mb-0 text-black">360-Degree Customer View</h3>
                      </div>
                      <p className="flex-grow-1 text-black-50">
                        Provides a consolidated perspective on customer preferences, transactions, and interactions 
                        empowers sales, marketing, and support teams to provide uniform and pertinent experiences 
                        across various touch points.
                      </p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
              <Col lg={6}>
                <motion.div 
                  variants={featureItem}
                  whileHover={{ scale: 1.01 }}
                >
                  <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '15px', backgroundColor: 'white', minHeight: '220px' }}>
                    <Card.Body className="p-4 d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <div className="me-3" style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '12px',
                          background: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '1.2rem'
                        }}>
                          <i className="bi bi-graph-up-arrow"></i>
                        </div>
                        <h3 className="fw-bold mb-0 text-black">Improved Sales Management</h3>
                      </div>
                      <p className="flex-grow-1 text-black-50">
                        Gives comprehensive insight into forecasts and pipelines, streamlining the sales process 
                        through an integrated solution that spans from generating quotes to order fulfillment 
                        and cash management.
                      </p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
              <Col lg={6}>
                <motion.div 
                  variants={featureItem}
                  whileHover={{ scale: 1.01 }}
                >
                  <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '15px', backgroundColor: 'white', minHeight: '220px' }}>
                    <Card.Body className="p-4 d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <div className="me-3" style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '12px',
                          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '1.2rem'
                        }}>
                          <i className="bi bi-headset"></i>
                        </div>
                        <h3 className="fw-bold mb-0 text-black">Enhanced Customer Service</h3>
                      </div>
                      <p className="flex-grow-1 text-black-50">
                        Empowers every customer-facing role, including sales, support, and service, with real-time 
                        access to updated data. It also ensures improved visibility for service and support representatives.
                      </p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
              <Col lg={6}>
                <motion.div 
                  variants={featureItem}
                  whileHover={{ scale: 1.01 }}
                >
                  <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '15px', backgroundColor: 'white', minHeight: '220px' }}>
                    <Card.Body className="p-4 d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <div className="me-3" style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '12px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '1.2rem'
                        }}>
                          <i className="bi bi-puzzle-fill"></i>
                        </div>
                        <h3 className="fw-bold mb-0 text-black">Integrated Ecosystem</h3>
                      </div>
                      <p className="flex-grow-1 text-black-50">
                        CRM is integrated into the broader suite of NetSuite applications, ensuring smooth coordination 
                        with other business functions such as finance, inventory, and order management.
                      </p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Understanding NetSuite CRM Section */}
      <section className="py-5" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
        <Container>
          {/* Enhanced Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-5"
          >
            {/* <div style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              padding: '0.5rem 1.5rem',
              borderRadius: '50px',
              color: 'white',
              fontSize: '0.9rem',
              fontWeight: 600,
              marginBottom: '1.5rem',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              <i className="bi bi-lightbulb me-2"></i>
              Platform Overview
            </div> */}
            
            <h2 style={{ 
              fontWeight: 800, 
              marginBottom: '1rem', 
              color: '#1e293b',
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              background: 'linear-gradient(135deg, #1e293b 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Understanding NetSuite CRM
            </h2>
            
            <p style={{ 
              fontSize: '1.2rem', 
              color: '#64748b',
              maxWidth: '700px',
              margin: '0 auto',
              lineHeight: 1.6
            }}>
              Discover how NetSuite CRM transforms business operations across industries with powerful, integrated solutions
            </p>
          </motion.div>

          <Row className="align-items-center g-5">
            {/* Left Content */}
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div style={{
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
                  padding: '2.5rem',
                  borderRadius: '20px',
                  border: '1px solid rgba(59, 130, 246, 0.1)'
                }}>
                  <div className="mb-4">
                    <div style={{
                      width: '60px',
                      height: '60px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1.5rem',
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)'
                    }}>
                      <i className="bi bi-diagram-3 text-white" style={{ fontSize: '1.8rem' }}></i>
                    </div>
                    
                    <h3 style={{ 
                      fontWeight: 700, 
                      marginBottom: '1rem',
                      color: '#1e293b',
                      fontSize: '1.5rem'
                    }}>
                      Versatile Industry Solution
                    </h3>
                    
                    <p style={{
                      fontSize: '1.1rem',
                      color: '#475569',
                      lineHeight: 1.7,
                      marginBottom: '2rem'
                    }}>
                      NetSuite CRM is a versatile solution employed across industries like IT, Professional Services, 
                      Retail, and more. It excels in streamlining multi-channel marketing campaigns and provides support 
                      teams with access to customer information—anytime, anywhere.
                    </p>
                  </div>

                  {/* Key Features */}
                  <div className="row g-3">
                    {[
                      { icon: 'bi-globe2', title: 'Multi-Channel Support', desc: 'Seamless integration across all platforms' },
                      { icon: 'bi-clock', title: '24/7 Access', desc: 'Real-time data availability' },
                      { icon: 'bi-shield-check', title: 'Enterprise Security', desc: 'Bank-level data protection' }
                    ].map((feature, index) => (
                      <div key={index} className="col-12">
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                          className="d-flex align-items-center"
                          style={{
                            padding: '1rem',
                            background: 'rgba(255, 255, 255, 0.8)',
                            borderRadius: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.5)'
                          }}
                        >
                          <div style={{
                            width: '40px',
                            height: '40px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '1rem',
                            flexShrink: 0
                          }}>
                            <i className={`bi ${feature.icon} text-white`} style={{ fontSize: '1.1rem' }}></i>
                          </div>
                          <div>
                            <h6 className="fw-bold mb-1" style={{ color: '#1e293b' }}>{feature.title}</h6>
                            <p className="mb-0 small" style={{ color: '#64748b' }}>{feature.desc}</p>
                          </div>
                        </motion.div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </Col>

            {/* Right Content - Why Choose SuiteMatrix */}
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Card style={{ 
                  border: 'none', 
                  borderRadius: '20px', 
                  backgroundColor: 'white',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                }}>
                  <Card.Body className="p-4">
                    {/* Card Header */}
                    <div style={{
                      textAlign: 'center',
                      marginBottom: '2rem',
                      paddingBottom: '1rem',
                      borderBottom: '2px solid #f1f5f9'
                    }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem',
                        boxShadow: '0 8px 32px rgba(245, 158, 11, 0.3)'
                      }}>
                        <i className="bi bi-award text-white" style={{ fontSize: '1.8rem' }}></i>
                      </div>
                      <h3 style={{ 
                        fontWeight: 700, 
                        color: '#1e293b',
                        fontSize: '1.5rem',
                        marginBottom: '0.5rem'
                      }}>
                        Why Choose SpireNest?
                      </h3>
                      <p style={{ 
                        color: '#64748b', 
                        fontSize: '1rem',
                        margin: 0
                      }}>
                        Your trusted NetSuite CRM partner
                      </p>
                    </div>

                    {/* Benefits List */}
                    <ul className="list-unstyled">
                      {[
                        "Deep understanding of your industry",
                        "Proven track record in NetSuite CRM solutions",
                        "Robust customization options for your workflows",
                        "Seamless integration with business systems",
                        "Ongoing support and maintenance services",
                        "Scalable solutions as your business grows",
                        "Robust security and compliance measures"
                      ].map((item, index) => (
                        <motion.li 
                          key={index}
                          initial={{ opacity: 0, x: 20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.08 }}
                          style={{
                            marginBottom: '1rem',
                            display: 'flex',
                            alignItems: 'flex-start',
                            padding: '0.75rem',
                            background: 'rgba(248, 250, 252, 0.5)',
                            borderRadius: '8px',
                            transition: 'all 0.3s ease'
                          }}
                          whileHover={{
                            backgroundColor: 'rgba(59, 130, 246, 0.05)',
                            x: 5
                          }}
                        >
                          <div style={{
                            width: '24px',
                            height: '24px',
                            background: index % 2 === 0 ? '#3b82f6' : '#10b981',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '0.75rem',
                            flexShrink: 0,
                            marginTop: '0.1rem'
                          }}>
                            <i className={`bi ${index % 2 === 0 ? 'bi-check' : 'bi-star'} text-white`} style={{ fontSize: '0.8rem' }}></i>
                          </div>
                          <span style={{
                            color: '#374151',
                            fontSize: '1.05rem',
                            fontWeight: 500,
                            lineHeight: 1.5
                          }}>
                            {item}
                          </span>
                        </motion.li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Latest Blogs Section */}
      <section className="py-5 bg-light">
        <Container>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-center mb-5 fw-bold">Latest Blogs</h2>
            <Row className="g-4">
              {[
                {
                  title: "NetSuite Cloud ERP for IPO Success",
                  excerpt: "Going public is a landmark achievement for any business. Beyond prestige, an IPO provides access to...",
                  color: "#74b9ff"
                },
                {
                  title: "Simplify Scheduling with NetSuite",
                  excerpt: "NetSuite's SuitePeople Workforce Management SuiteApp streamlines various aspects of...",
                  color: "#a29bfe"
                },
                {
                  title: "Sales Force Automation Guide",
                  excerpt: "What if you could empower your sales team to close more deals faster and smarter?",
                  color: "#55efc4"
                }
              ].map((blog, index) => (
                <Col md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -10 }}
                  >
                    <Card className="h-80 border-0 shadow-sm" style={{ borderRadius: '15px', backgroundColor: 'white', minHeight: '330px' }}>
                      <div style={{ 
                        height: '150px',
                        background: blog.color,
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <motion.div 
                          className="position-absolute rounded-circle"
                          style={{
                            width: '100px',
                            height: '100px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            top: '-20px',
                            right: '-20px'
                          }}
                          animate={{
                            scale: [1, 1.2, 1]
                          }}
                          transition={{
                            duration: 5,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        />
                      </div>
                      <Card.Body className="d-flex flex-column">
                        <h5 className="fw-bold mb-3 text-black">{blog.title}</h5>
                        <p className="text-muted flex-grow-1">{blog.excerpt}</p>
                        <motion.div
                          whileHover={{ x: 5 }}
                          transition={{ duration: 0.3 }}
                          className="mt-auto"
                        >
                          {/* <Button variant="link" className="ps-0 text-decoration-none">
                            Read more <i className="bi bi-arrow-right ms-1"></i>
                          </Button> */}
                        </motion.div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-5 text-white position-relative" 
        style={{ 
          background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
          overflow: 'hidden'
        }}>
        {/* Animated elements */}
        <motion.div 
          className="position-absolute rounded-circle"
          style={{
            width: '200px',
            height: '200px',
            top: '-50px',
            left: '-50px',
            background: 'rgba(255, 255, 255, 0.1)'
          }}
          animate={{
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <Container>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="fw-bold mb-4">Ready to Transform Your Business?</h2>
            <p className="lead mb-4">
              Discover how NetSuite CRM can revolutionize your customer relationships and sales processes.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button 
                variant="light" 
                size="lg" 
                className="rounded-pill px-4 py-2 shadow fw-bold"
              >
                 <a href="/contact" className="btn  btn-lg px-4">
                 Get Started Today <i className="bi bi-arrow-right ms-2"></i>
                </a>
                
                
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </section>
      <FAQsection />
    </div>
  );
};

export default NetSuiteCRM;